import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const Branch = () => {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingSubscriptionPlans, setLoadingSubscriptionPlans] = useState(false);
  const [subscriptionPlansError, setSubscriptionPlansError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [formData, setFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    EmergencyMobNo: '',
    password: '',
    businessCreatedDate: '',
    businessSubscriptionPlan: 'Standard',
    weeklyHoliday: ['Sunday'],
    address: '',
    businessLogo: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  useEffect(() => {
    fetchBranches();
    fetchSubscriptionPlans();
  }, []);

  const validateToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      return null;
    }
    
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        return null;
      }
      return token;
    } catch (error) {
      console.error('Invalid token:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('userRole');
      setError('Invalid session. Please log in again.');
      return null;
    }
  };

  const fetchBranches = async () => {
    const token = validateToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || localStorage.getItem('selectedCompanyId');
      
      if (!companyId) {
        setError('Company information not found. Please log in again.');
        setLoading(false);
        return;
      }

      const url = `https://customersupport-jhy9.onrender.com/api/companies/${companyId}/branches`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status < 500
      });

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        return;
      }

      const branchData = response.data;
      // Handle different response formats
      if (Array.isArray(branchData)) {
        setBranches(branchData);
      } else if (branchData && Array.isArray(branchData.branches)) {
        setBranches(branchData.branches);
      } else if (branchData && Array.isArray(branchData.data)) {
        setBranches(branchData.data);
      } else {
        console.log('Unexpected response format:', branchData);
        setBranches([]);
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to fetch branches.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    const token = validateToken();
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || localStorage.getItem('selectedCompanyId');
      
      console.log('🏢 Decoded token for company fetch:', {
        companyId,
        fullDecoded: decoded
      });

      if (!companyId) {
        console.error('❌ No company ID found in token or localStorage');
        setCompanies([]);
        return;
      }

      // Fetch the specific company that the user belongs to
      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status < 500
      });

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        return;
      }

      if (response.status >= 400) {
        console.error('❌ Error fetching company:', response.data);
        setCompanies([]);
        return;
      }

      const companyData = response.data;
      console.log('✅ Company data received:', companyData);
      
      // Set the single company in an array format for the dropdown
      if (companyData) {
        setCompanies([companyData]);
        // Auto-select this company in the form
        if (!formData.company) {
          setFormData(prev => ({
            ...prev,
            company: companyData._id
          }));
        }
      } else {
        setCompanies([]);
      }
    } catch (err) {
      console.error("❌ Error fetching company:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
      } else {
        setCompanies([]);
      }
    }
  };

  const fetchFranchises = async () => {
    const token = validateToken();
    if (!token) return;

    try {
      const response = await axios.get('https://customersupport-jhy9.onrender.com/api/franchises', {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status < 500
      });

      if (response.status === 401) {
        return;
      }

      const franchiseData = response.data;
      if (Array.isArray(franchiseData)) {
        setFranchises(franchiseData);
      } else if (franchiseData && Array.isArray(franchiseData.franchises)) {
        setFranchises(franchiseData.franchises);
      } else if (franchiseData && Array.isArray(franchiseData.data)) {
        setFranchises(franchiseData.data);
      } else {
        setFranchises([]);
      }
    } catch (err) {
      console.error("Error fetching franchises:", err);
    }
  };

  const fetchSubscriptionPlans = async () => {
    const token = validateToken();
    if (!token) {
      setSubscriptionPlansError('No access token found');
      return;
    }

    setLoadingSubscriptionPlans(true);
    setSubscriptionPlansError(null);

    try {
      console.log('🚀 FETCHING SUBSCRIPTION PLANS');
      console.log('├── Token available:', !!token);
      
      // First, try to get company/user ID from JWT token
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
      
      console.log('├── Decoded token for subscription plans:', {
        companyId,
        fullDecoded: decoded
      });

      // Try different API endpoints for subscription plans
      let response;
      let apiEndpoint;
      
      if (companyId) {
        // Try company-specific subscription plans first
        apiEndpoint = `https://customersupport-jhy9.onrender.com/api/subscription-plans/${companyId}`;
        console.log('├── Trying company-specific endpoint:', apiEndpoint);
        
        try {
          response = await axios.get(apiEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status < 500
          });
        } catch (err) {
          console.log('├── Company-specific endpoint failed, trying general endpoint');
        }
      }
      
      // If company-specific failed or no companyId, try general endpoint
      if (!response || response.status >= 400) {
        apiEndpoint = 'https://customersupport-jhy9.onrender.com/api/subscription-plans';
        console.log('├── Trying general endpoint:', apiEndpoint);
        
        response = await axios.get(apiEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        });
      }

      console.log('📡 SUBSCRIPTION PLANS API RESPONSE:');
      console.log('├── Status:', response.status);
      console.log('├── Headers:', response.headers);
      console.log('├── Data:', response.data);

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setSubscriptionPlansError('Session expired. Please log in again.');
        return;
      }

      if (response.status === 404) {
        console.log('⚠️ No subscription plans found');
        setSubscriptionPlans([]);
        setSubscriptionPlansError('No subscription plans available');
        return;
      }

      if (response.status >= 400) {
        console.error('❌ Error response:', response.data);
        setSubscriptionPlansError(`Error fetching subscription plans: ${response.data?.message || 'Server error'}`);
        return;
      }

      const planData = response.data;
      let processedPlans = [];

      // Handle different response formats
      if (Array.isArray(planData)) {
        processedPlans = planData;
      } else if (planData && Array.isArray(planData.subscriptionPlans)) {
        processedPlans = planData.subscriptionPlans;
      } else if (planData && Array.isArray(planData.data)) {
        processedPlans = planData.data;
      } else if (planData && Array.isArray(planData.plans)) {
        processedPlans = planData.plans;
      } else if (planData && typeof planData === 'object') {
        // Single plan object
        processedPlans = [planData];
      } else {
        console.log('⚠️ Unexpected response format:', planData);
        processedPlans = [];
      }

      console.log('🎯 PROCESSED SUBSCRIPTION PLANS:');
      console.log('├── Total plans found:', processedPlans.length);
      
      processedPlans.forEach((plan, index) => {
        console.log(`├── Plan ${index + 1}:`);
        console.log(`    ├── ID: ${plan._id || plan.id || 'N/A'}`);
        console.log(`    ├── Title/Name: ${plan.title || plan.name || plan.planName || 'N/A'}`);
        console.log(`    ├── Description: ${plan.description || 'N/A'}`);
        console.log(`    ├── Price: ${plan.price || plan.cost || plan.amount || 'N/A'}`);
        console.log(`    ├── Currency: ${plan.currency || 'USD'}`);
        console.log(`    ├── Duration: ${plan.duration || plan.period || plan.billingCycle || 'N/A'}`);
        console.log(`    ├── Features: ${plan.features ? plan.features.length : 'N/A'}`);
        console.log(`    ├── Active: ${plan.isActive !== undefined ? plan.isActive : 'N/A'}`);
        console.log(`    ├── Display Text: "${(plan.title || plan.name || plan.planName || 'Unknown Plan')} - ${plan.currency || '$'}${plan.price || plan.cost || plan.amount || '0'} (${plan.duration || plan.period || plan.billingCycle || 'N/A'})"}`);
        console.log(`    └── Company: ${plan.company || plan.companyId || 'N/A'}`);
      });

      setSubscriptionPlans(processedPlans);
      
      if (processedPlans.length === 0) {
        setSubscriptionPlansError('No subscription plans available');
      } else {
        console.log(`✅ Successfully loaded ${processedPlans.length} subscription plans`);
      }

    } catch (err) {
      console.error('❌ Error fetching subscription plans:', err);
      
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setSubscriptionPlansError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setSubscriptionPlansError('Subscription plans service not found');
      } else if (err.response?.status >= 500) {
        setSubscriptionPlansError('Server error. Please try again later.');
      } else {
        setSubscriptionPlansError(`Network error: ${err.message}`);
      }
    } finally {
      setLoadingSubscriptionPlans(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'weeklyHoliday') {
      if (checked) {
        setFormData(prev => ({
          ...prev,
          weeklyHoliday: [...prev.weeklyHoliday, value]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          weeklyHoliday: prev.weeklyHoliday.filter(day => day !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;
        setFormData(prev => ({
          ...prev,
          businessLogo: base64String
        }));
        setLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddBranch = async (e) => {
    e.preventDefault();
    
    const token = validateToken();
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      // Get company ID from JWT token
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || localStorage.getItem('selectedCompanyId');
      
      if (!companyId) {
        alert('Company information not found. Please log in again.');
        return;
      }

      const payload = {
        businessName: formData.businessName.trim(),
        businessEmail: formData.businessEmail.toLowerCase().trim(),
        businessPhone: formData.businessPhone.trim(),
        EmergencyMobNo: formData.EmergencyMobNo.trim(),
        password: formData.password,
        businessCreatedDate: formData.businessCreatedDate || new Date().toISOString(),
        businessSubscriptionPlan: formData.businessSubscriptionPlan,
        weeklyHoliday: formData.weeklyHoliday,
        address: formData.address.trim(),
        isBranch: true,
        parentCompanyId: companyId,
        ...(formData.businessLogo && { businessLogo: formData.businessLogo })
      };

      console.log('🚀 Creating branch with payload:', payload);

      const response = await axios.post(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}/branches`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        return;
      }

      if (response.status >= 400) {
        console.error('Error response:', response.data);
        alert(`Error creating branch: ${response.data.message || 'Please try again.'}`);
        return;
      }

      console.log("Branch created successfully:", response.data);
      fetchBranches();
      resetForm();
      alert('Branch created successfully!');
    } catch (error) {
      console.error("Error creating branch:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
      } else {
        alert(`Error creating branch: ${error.response?.data?.message || 'Please try again.'}`);
      }
    }
  };

  const handleEditBranch = (branch) => {
    setIsEditing(true);
    setEditingBranchId(branch._id);
    setFormData({
      businessName: branch.businessName || '',
      businessEmail: branch.businessEmail || '',
      businessPhone: branch.businessPhone || '',
      EmergencyMobNo: branch.EmergencyMobNo || '',
      password: '',
      businessCreatedDate: branch.businessCreatedDate ? branch.businessCreatedDate.split('T')[0] : '',
      businessSubscriptionPlan: branch.businessSubscriptionPlan || (subscriptionPlans.length > 0 ? (subscriptionPlans[0].title || subscriptionPlans[0].name || subscriptionPlans[0].planName) : 'Standard'),
      weeklyHoliday: branch.weeklyHoliday || ['Sunday'],
      address: branch.address || '',
      businessLogo: branch.businessLogo || ''
    });
    setLogoPreview(branch.businessLogo || null);
    setShowForm(true);
  };

  const handleUpdateBranch = async (e) => {
    e.preventDefault();
    
    const token = validateToken();
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      // Get company ID from JWT token
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || localStorage.getItem('selectedCompanyId');
      
      if (!companyId) {
        alert('Company information not found. Please log in again.');
        return;
      }

      const payload = {
        businessName: formData.businessName.trim(),
        businessEmail: formData.businessEmail.toLowerCase().trim(),
        businessPhone: formData.businessPhone.trim(),
        EmergencyMobNo: formData.EmergencyMobNo.trim(),
        businessCreatedDate: formData.businessCreatedDate || new Date().toISOString(),
        businessSubscriptionPlan: formData.businessSubscriptionPlan,
        weeklyHoliday: formData.weeklyHoliday,
        address: formData.address.trim(),
        isBranch: true,
        parentCompanyId: companyId,
        ...(formData.password && { password: formData.password }),
        ...(formData.businessLogo && { businessLogo: formData.businessLogo })
      };

      console.log('🔄 Updating branch with payload:', payload);

      const response = await axios.put(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}/branches/${editingBranchId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        return;
      }

      if (response.status >= 400) {
        console.error('Error response:', response.data);
        alert(`Error updating branch: ${response.data.message || 'Please try again.'}`);
        return;
      }

      console.log("Branch updated successfully:", response.data);
      fetchBranches();
      resetForm();
      alert('Branch updated successfully!');
    } catch (error) {
      console.error("Error updating branch:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
      } else {
        alert(`Error updating branch: ${error.response?.data?.message || 'Please try again.'}`);
      }
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) {
      return;
    }

    const token = validateToken();
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      // Get company ID from JWT token for delete endpoint
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || localStorage.getItem('selectedCompanyId');
      
      if (!companyId) {
        alert('Company information not found. Please log in again.');
        return;
      }

      const response = await axios.delete(
        `https://customersupport-jhy9.onrender.com/api/companies/${companyId}/branches/${branchId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        return;
      }

      if (response.status >= 400) {
        alert(`Error deleting branch: ${response.data?.message || 'Please try again.'}`);
        return;
      }

      fetchBranches();
      alert('Branch deleted successfully!');
    } catch (error) {
      console.error("Error deleting branch:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
      } else {
        alert(`Error deleting branch: ${error.response?.data?.message || 'Please try again.'}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      businessName: '',
      businessEmail: '',
      businessPhone: '',
      EmergencyMobNo: '',
      password: '',
      businessCreatedDate: '',
      businessSubscriptionPlan: subscriptionPlans.length > 0 ? (subscriptionPlans[0].title || subscriptionPlans[0].name || subscriptionPlans[0].planName) : 'Standard',
      weeklyHoliday: ['Sunday'],
      address: '',
      businessLogo: ''
    });
    setLogoPreview(null);
    setShowForm(false);
    setIsEditing(false);
    setEditingBranchId(null);
  };

  const handleViewBranch = (branch) => {
    setSelectedBranch(branch);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedBranch(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading branches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Branch Management</h1>
            <p className="text-gray-600 mt-2">Manage all branches in your system</p>
          </div>
          <button
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            {showForm ? 'Cancel' : 'Add Branch'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Branch' : 'Add New Branch'}</h2>
            <form onSubmit={isEditing ? handleUpdateBranch : handleAddBranch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* First Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Email *</label>
                  <input
                    type="email"
                    name="businessEmail"
                    value={formData.businessEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="branch@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Phone</label>
                  <input
                    type="text"
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+1-555-123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Mobile No</label>
                  <input
                    type="text"
                    name="EmergencyMobNo"
                    value={formData.EmergencyMobNo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+1-555-765-4321"
                  />
                </div>
              </div>

              {/* Second Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password {isEditing ? '' : '*'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={isEditing ? 'Enter new password (optional)' : 'Set a password'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Created Date</label>
                  <input
                    type="date"
                    name="businessCreatedDate"
                    value={formData.businessCreatedDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan
                    {loadingSubscriptionPlans && (
                      <span className="ml-2 text-xs text-blue-600">
                        <svg className="inline w-3 h-3 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </span>
                    )}
                  </label>
                  <select
                    name="businessSubscriptionPlan"
                    value={formData.businessSubscriptionPlan}
                    onChange={handleInputChange}
                    disabled={loadingSubscriptionPlans}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {loadingSubscriptionPlans ? (
                      <option value="">Loading subscription plans...</option>
                    ) : subscriptionPlansError ? (
                      <option value="Standard">Standard (Default)</option>
                    ) : subscriptionPlans.length === 0 ? (
                      <>
                        <option value="Basic">Basic</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                        <option value="Enterprise">Enterprise</option>
                      </>
                    ) : (
                      subscriptionPlans.map(plan => {
                        const planTitle = plan.title || plan.name || plan.planName || 'Unknown Plan';
                        const planPrice = plan.price || plan.cost || plan.amount;
                        const planCurrency = plan.currency || '₹';
                        const planDuration = plan.duration || plan.period || plan.billingCycle;
                        
                        // Create display text with proper formatting
                        let displayText = planTitle;
                        if (planPrice) {
                          displayText += ` - ${planCurrency}${planPrice}`;
                        }
                        if (planDuration) {
                          displayText += ` (${planDuration})`;
                        }
                        
                        return (
                          <option key={plan._id || plan.id} value={planTitle}>
                            {displayText}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {subscriptionPlansError && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {subscriptionPlansError}
                    </p>
                  )}
                  {!loadingSubscriptionPlans && !subscriptionPlansError && subscriptionPlans.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-green-600 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {subscriptionPlans.length} subscription plans available
                      </p>
                      {formData.businessSubscriptionPlan && (() => {
                        const selectedPlan = subscriptionPlans.find(plan => 
                          (plan.title || plan.name || plan.planName) === formData.businessSubscriptionPlan
                        );
                        if (selectedPlan) {
                          return (
                            <div className="mt-1 p-2 bg-blue-50 rounded-md border border-blue-200">
                              <p className="text-xs text-blue-800 font-medium">
                                📋 Selected Plan Details:
                              </p>
                              <div className="text-xs text-blue-700 mt-1 space-y-1">
                                <div><strong>Title:</strong> {selectedPlan.title || selectedPlan.name || selectedPlan.planName}</div>
                                {(selectedPlan.price || selectedPlan.cost || selectedPlan.amount) && (
                                  <div><strong>Price:</strong> {selectedPlan.currency || '$'}{selectedPlan.price || selectedPlan.cost || selectedPlan.amount}</div>
                                )}
                                {(selectedPlan.duration || selectedPlan.period || selectedPlan.billingCycle) && (
                                  <div><strong>Duration:</strong> {selectedPlan.duration || selectedPlan.period || selectedPlan.billingCycle}</div>
                                )}
                                {selectedPlan.description && (
                                  <div><strong>Description:</strong> {selectedPlan.description}</div>
                                )}
                                {selectedPlan.features && selectedPlan.features.length > 0 && (
                                  <div><strong>Features:</strong> {selectedPlan.features.length} features included</div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Weekly Holiday</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                      <label key={day} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="weeklyHoliday"
                          value={day}
                          checked={formData.weeklyHoliday.includes(day)}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Third Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="123 Main St, Downtown City, State, ZIP"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="w-20 h-20 object-cover rounded-md border border-gray-300"
                      />
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Max file size: 5MB</p>
                </div>
              </div>

              <div className="md:col-span-2 lg:col-span-3 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {isEditing ? 'Update Branch' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Branch List</h2>
            <p className="text-gray-600 text-sm">{branches.length} branches found</p>
          </div>

          {branches.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0h-2m0-3h.01M17 18h.01M14 18h.01" />
              </svg>
              <p className="mt-4 text-gray-600">No branches found. Add your first branch to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Plan</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {branches.map((branch, index) => (
                    <tr key={branch._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {branch.businessLogo && (
                            <img
                              src={branch.businessLogo}
                              alt="Logo"
                              className="w-8 h-8 rounded-full mr-3 object-cover"
                            />
                          )}
                          <span>{branch.businessName || branch.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{branch.businessEmail || branch.email || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{branch.businessPhone || branch.phone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          branch.businessSubscriptionPlan === 'Enterprise' ? 'bg-purple-100 text-purple-800' :
                          branch.businessSubscriptionPlan === 'Premium' ? 'bg-blue-100 text-blue-800' :
                          branch.businessSubscriptionPlan === 'Standard' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {branch.businessSubscriptionPlan || 'Standard'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          {branch.isBranch ? 'Branch' : 'Company'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewBranch(branch)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleEditBranch(branch)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteBranch(branch._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Branch Modal */}
        {showViewModal && selectedBranch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800">Branch Details</h2>
                <button
                  onClick={closeViewModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Business Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Business Information</h4>
                    
                    {selectedBranch.businessLogo && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Business Logo</label>
                        <img
                          src={selectedBranch.businessLogo}
                          alt="Business Logo"
                          className="w-16 h-16 object-cover rounded-md border border-gray-300"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Business Name</label>
                      <p className="text-gray-800 font-medium">{selectedBranch.businessName || selectedBranch.name || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Business Email</label>
                      <p className="text-gray-800">{selectedBranch.businessEmail || selectedBranch.email || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Business Phone</label>
                      <p className="text-gray-800">{selectedBranch.businessPhone || selectedBranch.phone || 'Not provided'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Emergency Mobile No</label>
                      <p className="text-gray-800">{selectedBranch.EmergencyMobNo || 'Not provided'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                      <p className="text-gray-800">{selectedBranch.address || 'Not provided'}</p>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Additional Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Business Created Date</label>
                      <p className="text-gray-800">
                        {selectedBranch.businessCreatedDate 
                          ? new Date(selectedBranch.businessCreatedDate).toLocaleDateString()
                          : 'Not provided'
                        }
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Subscription Plan</label>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        selectedBranch.businessSubscriptionPlan === 'Enterprise' ? 'bg-purple-100 text-purple-800' :
                        selectedBranch.businessSubscriptionPlan === 'Premium' ? 'bg-blue-100 text-blue-800' :
                        selectedBranch.businessSubscriptionPlan === 'Standard' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedBranch.businessSubscriptionPlan || 'Standard'}
                      </span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Weekly Holiday</label>
                      <div className="flex flex-wrap gap-1">
                        {(selectedBranch.weeklyHoliday || ['Sunday']).map(day => (
                          <span key={day} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Branch Status</label>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {selectedBranch.isBranch ? 'Branch' : 'Company'}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Branch ID</label>
                      <p className="text-gray-800 font-mono text-sm">{selectedBranch._id}</p>
                    </div>

                    {selectedBranch.parentCompanyId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Parent Company ID</label>
                        <p className="text-gray-800 font-mono text-sm">{selectedBranch.parentCompanyId}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Branch;
